import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RefreshCcw, CheckCircle2, XCircle, HelpCircle, Loader2 } from 'lucide-react';
import { getAiStatus } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * AI Status Indicator
 *
 * Shows a green ✅ / red ❌ / grey ⏳ card so the user immediately knows
 * if the AI features (Coach, Food analysis, Body composition, Voice) are
 * functional — instead of discovering a budget-exhausted Universal Key
 * mid live-demo. Pings backend every 5 min + manual refresh button.
 *
 * Designed for the Settings panel.
 */
export const AiStatusCard = () => {
    const { language } = useLanguage();
    const [status, setStatus] = useState(null);   // 'ok' | 'down' | 'unknown' | null
    const [error, setError] = useState(null);
    const [checkedAt, setCheckedAt] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStatus = useCallback(async (force = false) => {
        setLoading(true);
        try {
            const { data } = await getAiStatus(force);
            setStatus(data.status);
            setError(data.error || null);
            setCheckedAt(data.checked_at || null);
        } catch (e) {
            setStatus('unknown');
            setError(language === 'de' ? 'Status konnte nicht geladen werden' : 'Could not load status');
        } finally {
            setLoading(false);
        }
    }, [language]);

    useEffect(() => {
        fetchStatus(false);
        const interval = setInterval(() => fetchStatus(false), 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchStatus]);

    const isOk = status === 'ok';
    const isDown = status === 'down';
    const Icon = loading ? Loader2 : isOk ? CheckCircle2 : isDown ? XCircle : HelpCircle;
    const tone = isOk
        ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
        : isDown
            ? 'text-red-600 bg-red-50 border-red-200'
            : 'text-amber-600 bg-amber-50 border-amber-200';

    const headline = isOk
        ? (language === 'de' ? 'KI funktioniert' : 'AI is healthy')
        : isDown
            ? (language === 'de' ? 'KI nicht erreichbar' : 'AI unavailable')
            : (language === 'de' ? 'KI-Status unbekannt' : 'AI status unknown');

    const niceTime = checkedAt
        ? new Date(checkedAt).toLocaleTimeString(language === 'de' ? 'de-DE' : 'en-US', { hour: '2-digit', minute: '2-digit' })
        : '—';

    // Friendlier explanation for the most common cause
    const isBudgetIssue = isDown && error && /budget/i.test(error);
    const helpText = isBudgetIssue
        ? (language === 'de'
            ? 'Dein Universal Key hat das Max-Budget-Limit erreicht. Lade Credits nach oder erhöhe das Limit in Profil → Universal Key.'
            : 'Your Universal Key has hit its max budget. Top up credits or raise the limit in Profile → Universal Key.')
        : isDown
            ? (language === 'de' ? 'KI-Calls schlagen gerade fehl. Prüfe Verbindung und LLM-Key.' : 'AI calls are failing right now. Check connection and LLM key.')
            : isOk
                ? (language === 'de' ? 'Coach, Foto-Analyse und alle KI-Features sind einsatzbereit.' : 'Coach, photo analysis and all AI features are ready.')
                : (language === 'de' ? 'Klicke auf Aktualisieren, um den Status zu prüfen.' : 'Click refresh to check the status.');

    return (
        <Card data-testid="ai-status-card">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    {language === 'de' ? 'KI-Status' : 'AI Status'}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div
                    className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${tone}`}
                    data-testid="ai-status-pill"
                    data-status={status || 'loading'}
                >
                    <Icon className={`w-6 h-6 flex-shrink-0 mt-0.5 ${loading ? 'animate-spin' : ''}`} />
                    <div className="flex-1">
                        <p className="font-semibold" data-testid="ai-status-headline">{headline}</p>
                        <p className="text-sm opacity-90 mt-0.5">{helpText}</p>
                        {error && (
                            <p className="text-xs opacity-75 mt-2 font-mono break-all" data-testid="ai-status-error">{error}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                        {language === 'de' ? 'Zuletzt geprüft' : 'Last checked'}: <span className="font-medium">{niceTime}</span>
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchStatus(true)}
                        disabled={loading}
                        data-testid="ai-status-refresh"
                    >
                        <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                        {language === 'de' ? 'Aktualisieren' : 'Refresh'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};
